import { canopyWebOrigin } from './environmentUtils';

export const LANDING_PAGE_ROUTE = '/login';

export const SPARKRX_PDT_UID = '8dpNPSmSnyN3vxeBi4RtM9';
export const SLEEPIO_PDT_UID = 'BxgXBrgwswcX58wFNwhVfv';
export const DAYLIGHT_PDT_UID = 'DhZzR9fNJyNGMETp6Qgr6K';
export const DAYLIGHT_PLATGEN_PDT_UID = 'WcLGEey8ZMtnpoKBDrWH26';

// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s01.html
// eslint-disable-next-line
export const EMAIL_VALIDATION_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

// RTX's internal authentication alias domain — can't receive mail.
// Only supports one domain today; can be extended to a list later if needed.
export const BLOCKED_SIGNUP_EMAIL_DOMAIN = 'adxuser.com';

// https://ihateregex.io/expr/phone/
export const PHONE_VALIDATION_REGEX = /^[+]?\(?\d{3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/i;

// Validates this format: MM/DD/YYYY
export const DOB_VALIDATION_REGEX = /(?:(09|04|06|11)(\/)(0[1-9]|[12]\d|30)(\/)((?:19|20)\d\d))|(?:(01|03|05|07|08|10|12)(\/)(0[1-9]|[12]\d|3[01])(\/)((?:19|20)\d\d))|(?:02(\/)(?:(?:(0[1-9]|1\d|2[0-8])(\/)((?:19|20)\d\d))|(?:(29)(\/)((?:(?:19|20)(?:04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96))|2000))))/; // eslint-disable-line

// Validates zipcodes in these formats: #####
export const ZIPCODE_VALIDATION_REGEX = /^(\d{5})$/;

/* Validates password with these rules:
 - 8-24 characters
 - Include letters, numbers, and special characters
 */
export const PASSWORD_VALIDATION_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,24}$/;

export const EN = 'EN';
export const ES = 'ES';

export const LANGUAGE_LIST = [
  { value: EN, label: 'English' },
  { value: ES, label: 'Español' },
];

export const MINIMUM_BIRTH_YEAR = 1900;

export const SINGULAR_SDK_KEY = 'bighealth_8b9fd55c';
export const SINGULAR_SDK_SECRET = 'c65265155b6d1a8de24c00a5570aad38';

export const EMPLOYER_ORG_SLUG_KEY = 'employer_org_slug';
export const REDIRECT_TO_SESSION_KEY = 'cap_redirect_to';

/**
 * Every address CAP will hand a finished Sleepio session to, in full.
 *
 * Mirrors `VALID_REDIRECT_TARGETS` in vercel-auth-functions `lib/redirectAllowlist.ts` — one
 * entry per scheme the Sleepio app registers. There is no package shared between the two
 * repos, so this is the second of two statements of the same set; change one and the other
 * has to move with it. Both repos' contract tests write the strings out in full so a
 * disagreement shows up as a diff rather than as a failed sign-in.
 *
 * **Exact match, not "starts with an allowed scheme."** A scheme check admits
 * `sleepio://anything?attacker=controlled`, and this is the value the one-time exchange token
 * gets attached to. The auth service also checks relayed addresses, but a phone-OTP sign-in
 * sends no email, so on that path this list alone decides where the session goes.
 */
/**
 * The one query parameter a handoff address may carry: the app's per-flow nonce.
 *
 * The app mints an unguessable value when it starts a sign-in, appends it to the address it asks
 * us to send it back to, and refuses any handoff that does not carry it back. That is what binds
 * a returning session to the flow that asked for it.
 *
 * CAP never *mints* or *checks* it — the binding lives entirely on the device, which compares an
 * arrival against the set that install generated. CAP reads it only nested inside `redirectTo`;
 * a flat `hn` on CAP's callback URL is ignored.
 *
 * Mirrors `HANDOFF_NONCE_PARAM` in vercel-auth-functions `lib/redirectAllowlist.ts`; both repos
 * write the name out so a disagreement shows up as a diff. **Optional by design** — an app build
 * that predates the nonce sends no query and stays legal, which it must, because an installed app
 * is never fully replaced.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THIS PATH IS FORWARD-ONLY. READ THIS BEFORE ROLLING CAP BACK.
 *
 * Canopy builds from PAT-1286 onwards **refuse any handoff carrying no nonce** — the check is
 * `matchesCapHandoffNonce`, and an absent value answers "no match" by design, at three separate
 * token readers. A CAP version that predates the change never emits `hn` at all.
 *
 * So rolling CAP back past the PAT-1286 change (or cutting a hotfix branch from before it, or
 * leaving an environment on an older deploy) **refuses every sign-in for every updated app**. Not
 * degraded: refused. The member lands on the activation screen each time.
 *
 * And it cannot be fixed from the app side quickly. The installed base is never fully replaced, so
 * there is no "roll the app back"; the remedy is a code change in canopy plus a release. At the
 * time of writing the over-the-air escape hatch is **not available in production**: `updates.url`
 * and `runtimeVersion: { policy: 'appVersion' }` are configured in `apps/sleepio-expo/app.config.js`,
 * but the `production` build profile in `eas.json` declares no `channel` (only `dev` and `stage` do),
 * so a published update has nothing to reach. Confirm that with whoever owns the release pipeline
 * before counting on an OTA push.
 *
 * If CAP genuinely has to go back:
 *   1. Do not. Roll forward — fix the defect on top of the PAT-1286 change instead.
 *   2. If it is unavoidable, the app change needed is to stop requiring the nonce, in
 *      `packages/app/features/sleepio-activation/capHandoffNonce.native.ts` and its three callers
 *      (`useAuthCallback.native.ts`, `app/(auth)/exchange-callback.tsx`, `app/(auth)/activate.tsx`).
 *      That reopens the hole PAT-1286 closed — a link carrying a token an attacker minted against
 *      their own account signs a member into it — so it is an incident-grade tradeoff, not a config
 *      flip.
 *   3. Wiring a `channel` onto the production profile would turn step 2 into an OTA push rather
 *      than an App Store release. Worth doing regardless of this ticket.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */
export const HANDOFF_NONCE_PARAM = 'hn';

export const CANOPY_POST_AUTH_CALLBACK_TARGETS = [
  'sleep-companion://exchange-callback',
  'sleepio://exchange-callback',
] as const;

/**
 * Canopy web origins a finished session may go to: per environment (`canopyWebOrigin()`), origin
 * only so Canopy picks the path, and empty where web isn't deployed (stage, prod). On dev, qa and
 * pr, localhost is allowed too, for testing against a local web app.
 */
const LOCAL_CANOPY_WEB_ORIGINS = ['http://localhost:3000', 'http://localhost:3001'];

// Named explicitly so an unset or unknown deploy env gets no localhost. No `stage`: it has no web.
const NON_PROD_DEPLOY_ENVS = ['local', 'dev', 'qa', 'pr'];

export const canopyWebOrigins = (): readonly string[] => {
  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  // envSwitch is typed string, but an unknown deploy env returns undefined.
  const rawOrigin = (canopyWebOrigin() as string | undefined)?.trim().replace(/\/+$/, '');
  // The exchange token is appended to this address, so only https (or exact localhost) counts.
  const tableOrigin = rawOrigin && (rawOrigin.startsWith('https://') || LOCAL_CANOPY_WEB_ORIGINS.includes(rawOrigin))
    ? rawOrigin
    : undefined;

  const origins = tableOrigin ? [tableOrigin] : [];
  if (NON_PROD_DEPLOY_ENVS.includes(deployEnv as string)) {
    // Filtered: `local` already has localhost from the table.
    origins.push(...LOCAL_CANOPY_WEB_ORIGINS.filter((origin) => !origins.includes(origin)));
  }

  return origins;
};

/**
 * Stand-in for the `:org_slug` segment on the Sleepio login route.
 *
 * A login magic link carries no organisation — vercel-auth-functions builds that URL and
 * has none to put in it — but the route pattern requires the segment, and an empty one
 * leaves a `//` that matches nothing. The login path never reads the value, so any
 * non-empty string works; this is named so it can be grepped and so a Cortex-facing call
 * can assert it never receives it.
 */
export const LOGIN_ORG_SLUG_PLACEHOLDER = 'unknown';
export const ACCESS_CODE_SESSION_KEY = 'cap_access_code';
export const DEPENDENT_ACTIVATION_DATA_KEY = 'dependent_activation_data';

export type LineOfBusiness = 'wellness' | 'rx';

/**
 * Sleepio Activation Flow A/B Experiment
 *
 * - VARIANT_A: LineStep - horizontal progress bar with 3 lines
 * - VARIANT_B: ListStep - numbered list showing steps with durations
 */
export const VARIANT_A = 'variant-a';
export const VARIANT_B = 'variant-b';

export const ONBOARDING_STEPS = [
  { titleKey: 'onboarding.steps.verify_email', durationKey: 'onboarding.steps.duration_30_seconds' },
  { titleKey: 'onboarding.steps.confirm_coverage', durationKey: 'onboarding.steps.duration_1_minute' },
  { titleKey: 'onboarding.steps.download_sleepio', durationKey: 'onboarding.steps.duration_1_minute' },
];
